import React, { useEffect, useState } from 'react';

import If from '@components/shared/conditional/If/If';
import { ImageAnnotations } from '@components/shared/ImageAnnotation';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import Image from 'next/image';
import { imageLoader } from '@lib/image';
import CarouselImagePlaceholder from '../CarouselImagePlaceholder';

import styles from './CarouselImage.module.scss';

interface CarouselImageProps {
  alt: string;
  aspectRatio?: number;
  className?: string;
  firstImage: boolean;
  onError?: (e: any) => void;
  onLoad?: (e: any) => void;
  url: string;
  wrapperStyles?: any;
  annotation?: {
    assetId: string;
    annotations: any[];
    toggleOn: boolean;
  };
}

export default function CarouselImage(props: CarouselImageProps) {
  const {
    alt,
    aspectRatio = 10 / 16,
    className = '',
    firstImage,
    onError = () => {},
    onLoad = () => {},
    url,
    wrapperStyles = {},
  } = props;
  const { annotation } = props;
  const assetId = (annotation) ? annotation.assetId : undefined;
  const annotations = (annotation) ? annotation.annotations : undefined;
  const toggleOn = (annotation) ? annotation.toggleOn : undefined;
  const [imageLoaded, setImageLoaded] = useState(false);

  /**
   * I noticed a misalignment of bubbles on the client-side board details page.
   * It appears that when we run the images through thumbor, it does its intelligent cropping which
   * causes misalignment depending on how extreme the cropping is.
   *
   * This issue does not occur if we maintain the original aspect ratio of the image.
   *
   * For now the team decided to move forward because the drift is relatively subtle here.
   * In the future we may need to come up with a solution which allows us to modify
   * image aspect ratio without causing the product bubbles to misalign.
   *
   * Maybe making the image a background with a constrained wrapper and an overflow: hidden;
   * applied or something like that.
   *
   * Love,
   * Nick
   */

  useEffect(() => {
    setImageLoaded(false);
  }, [url]);

  const handleOnLoad = (e: any) => {
    setImageLoaded(true);
    if (onLoad) {
      onLoad(e);
    }
  };

  const handleOnError = (e: any) => {
    setImageLoaded(true);
    if (onError) {
      onError(e);
    }
  };

  return (
    <div
      className={ `
        ${styles.ImageWrapper}
        ${imageLoaded ? styles.isLoaded : ''}
      ` }
      style={{ ...wrapperStyles }}
    >
      <AspectRatioBox aspectRatio={ aspectRatio }>
        <div
          className={`
              ${styles.CarouselImage}
              ${imageLoaded ? styles.isLoaded : ''}
              ${className}
            `}
        >
          <Image
            alt={alt}
            onError={handleOnError}
            onLoad={handleOnLoad}
            layout="fill"
            priority={firstImage}
            quality={50}
            src={url}
            loader={imageLoader}
          />
        </div>
        <If condition={!!toggleOn && !!annotations && annotations.length > 0}>
          <ImageAnnotations annotations={annotations} assetId={assetId} altInfoBubble />
        </If>
        <If condition={ !imageLoaded }>
          <CarouselImagePlaceholder
            aspectRatio={ aspectRatio }
            className={ className }
          />
        </If>
      </AspectRatioBox>
    </div>
  );
}
